import { createContext, useContext, useMemo } from 'react';
import { parseLocale } from '../../domain/i18n/locales';
import { findMarket } from '../../domain/i18n/markets';
import { interpolate, translate } from '../../domain/i18n/messages';

const LocaleContext = createContext(null);

/**
 * Puts the active locale — and the `t()` bound to its language — in context.
 *
 * The locale comes from the URL, never from state: the address bar is the single
 * source of truth for which market and language are on screen, so a shared link
 * always opens in the locale it was written in.
 */
export function LocaleProvider({ localeCode, children }) {
    const value = useMemo(() => {
        const parsed = parseLocale(localeCode);

        if (!parsed) {
            return null;
        }

        return {
            ...parsed,
            market: findMarket(parsed.marketCode),
            // The second argument fills `{placeholders}`; omitting it is the
            // common case and behaves exactly as it always has.
            t: (messageKey, values) => (
                values
                    ? interpolate(translate(parsed.languageCode, messageKey), values)
                    : translate(parsed.languageCode, messageKey)
            ),
        };
    }, [localeCode]);

    if (!value) {
        return null;
    }

    return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
    const value = useContext(LocaleContext);

    if (!value) {
        throw new Error('useLocale must be used inside a LocaleProvider');
    }

    return value;
}

/** `t('nav.plants')` for components that need copy but not the locale itself. */
export function useTranslate() {
    return useLocale().t;
}
